import { Pipe, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateService } from '../services/date/date.service';
import { DateFormatterService } from '../services/date/date-formatter.service';
import { TimezoneService } from '../services/date/timezone.service';
import type { PipeTransform } from '@angular/core';
import type { DateTimeFormatType } from '../services/date/date-formatter.service';

export interface DatePipeParameters {
  format?: DateTimeFormatType;
  emptyPlaceholder?: string;
  nullPlaceholder?: string;
  relative?: boolean;
}

type RelativeType = 'MINUTES' | 'HOURS' | 'DAYS' | 'DATE';

interface RelativeTimeFrame {
  value: number | Date;
  valueType: RelativeType;
}

/**
 * Formats date object. When DatePipeParameters.relative set to true value will be treated as a relative time frame (to now)
 * and formatted as e.g. "3 minutes ago" or "5 hours ago".
 */

@Pipe({
  name: 'tdWbDate'
})
export class DatePipe implements PipeTransform {
  private readonly timezoneService = inject<TimezoneService>(TimezoneService);
  private readonly dateFormatterService = inject<DateFormatterService>(DateFormatterService);
  private readonly translateService = inject<TranslateService>(TranslateService);

  private static readonly MS_PER_MINUTE: number = 60 * 1000;
  private static readonly MS_PER_HOUR: number = 60 * 1000 * 60;
  private static readonly MS_PER_DAY: number = 60 * 1000 * 60 * 24;
  private static readonly MS_PER_3DAYS: number = 60 * 1000 * 60 * 24 * 3;

  transform(value: Date | undefined, pipeParameters?: DatePipeParameters): string {
    const formatString: DateTimeFormatType =
      pipeParameters !== undefined && pipeParameters.format !== undefined ? pipeParameters.format : 'DATE';
    let formattedValue: string;

    if (value === undefined) {
      if (pipeParameters !== undefined && pipeParameters.emptyPlaceholder !== undefined) {
        formattedValue = pipeParameters.emptyPlaceholder;
      } else {
        formattedValue = this.translateService.instant('tdWb.core.pipes.NA');
      }
    } else if (value === null) {
      if (pipeParameters !== undefined && pipeParameters.nullPlaceholder !== undefined) {
        formattedValue = pipeParameters.nullPlaceholder;
      } else {
        formattedValue = this.translateService.instant('tdWb.core.pipes.NA');
      }
    } else if (pipeParameters !== undefined && pipeParameters.relative) {
      return this.getRelativeCaption(value, pipeParameters.format);
    } else {
      formattedValue = this.dateFormatterService.formatDate(value, formatString);
    }
    return formattedValue;
  }

  private getRelativeCaption(timestamp: Date | undefined, formatString?: DateTimeFormatType): string {
    let relativeTimeFrame: RelativeTimeFrame;

    if (timestamp !== null && timestamp !== undefined) {
      relativeTimeFrame = this.getRelativeTimeFrame(timestamp);
      return this.getCaption(relativeTimeFrame.valueType, relativeTimeFrame.value, formatString);
    }
  }

  private getCaption(relativeType: RelativeType, value: number | Date, formatString: DateTimeFormatType = 'YEAR_MONTH_DAY'): string {
    let key: string;
    switch (relativeType) {
      case 'MINUTES':
        return this.translateService.instant('tdWb.core.pipes.date.MINUTES_AGO', { value });
      case 'HOURS':
        key = value === 1 ? 'tdWb.core.pipes.date.ONE_HOUR_AGO' : 'tdWb.core.pipes.date.HOURS_AGO';
        return this.translateService.instant(key, { value });
      case 'DAYS':
        key = value === 1 ? 'tdWb.core.pipes.date.ONE_DAY_AGO' : 'tdWb.core.pipes.date.DAYS_AGO';
        return this.translateService.instant(key);
      case 'DATE':
        return this.dateFormatterService.formatDate(value as Date, formatString);
    }
  }

  private getRelativeTimeFrame(date: Date): RelativeTimeFrame {
    const dateInServerTimezone: Date = this.timezoneService.getCurrentDateInServerTimezone();
    const timeDifference: number = DateService.getUTCTimeDifferenceInMilliseconds(date, dateInServerTimezone);

    if (timeDifference < DatePipe.MS_PER_HOUR) {
      return { value: Math.floor(timeDifference / DatePipe.MS_PER_MINUTE), valueType: 'MINUTES' };
    }
    if (timeDifference < DatePipe.MS_PER_DAY) {
      return { value: Math.floor(timeDifference / DatePipe.MS_PER_HOUR), valueType: 'HOURS' };
    }
    if (timeDifference < DatePipe.MS_PER_3DAYS) {
      return { value: Math.floor(timeDifference / DatePipe.MS_PER_DAY), valueType: 'DAYS' };
    }
    return { value: date, valueType: 'DATE' };
  }

}
